import { apiDownloadFile, apiFetch } from "@/services/apiClient";
import { buildDrillThroughParams } from "@/lib/drillThroughTableQuery";
import { appendSortParams, type SortState } from "@/lib/tableSort";
import { API_URL } from "@/lib/utils";

export interface LockupFilters {
  service_lines: string[];
  legacy_locations: string[];
  managers: string[];
  assignment_categories: string[];
  age_band_names: string[];
  date_from: string | null;
  date_to: string | null;
}

export interface LockupFilterOptions {
  service_lines: string[];
  legacy_locations: string[];
  managers: string[];
  assignment_categories: string[];
  age_band_names: string[];
  min_month: string | null;
  max_month: string | null;
}

export type LockupDrillThroughRow = {
  assignment_manager?: string | null;
  client_code?: string | null;
  client_name?: string | null;
  assignment_code?: string | null;
  assignment_name?: string | null;
  grade?: string | null;
  in_wip?: boolean;
  in_debtors?: boolean;
  wip: LockupValueBucket;
  aged_debt: LockupValueBucket;
  lockup: LockupValueBucket;
};

export type LockupValueBucket = Record<string, number | null | undefined> & {
  total: number;
};

/** One (client, assignment) pair. Rows for the same client are adjacent and pre-sorted by the API. */
export type LockupClientAssignmentRow = {
  client_code?: string | null;
  client_name?: string | null;
  assignment_code?: string | null;
  assignment_name?: string | null;
  wip: LockupValueBucket;
  aged_debt: LockupValueBucket;
  lockup: LockupValueBucket;
};

export interface LockupDrillThroughResponse {
  wip_as_at_date: string | null;
  debtors_as_at_date: string | null;
  monthLabel: string | null;
  age_bands: Array<string | { key?: string; label?: string; name?: string }>;
  total_wip_gbp: number;
  total_aged_debt_gbp: number;
  total_lockup_gbp: number;
  by_assignment_manager: LockupDrillThroughRow[];
  by_client_assignment: LockupClientAssignmentRow[];
  assignment_manager_total_rows: number;
  client_assignment_total_rows: number;
}

function buildQs(f: LockupFilters): string {
  const p = new URLSearchParams();
  f.service_lines.forEach(v => p.append("service_lines", v));
  f.legacy_locations.forEach(v => p.append("legacy_locations", v));
  f.managers.forEach(v => p.append("managers", v));
  f.assignment_categories.forEach(v => p.append("assignment_categories", v));
  f.age_band_names.forEach(v => p.append("age_band_names", v));
  if (f.date_from) p.append("date_from", f.date_from);
  if (f.date_to)   p.append("date_to",   f.date_to);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await apiFetch(`${API_URL}${path}`, { method: "GET", signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export const fetchLockupFilterOptions = (
  filters: LockupFilters = { service_lines: [], legacy_locations: [], managers: [], assignment_categories: [], age_band_names: [], date_from: null, date_to: null },
  signal?: AbortSignal,
) =>
  getJson<Omit<LockupFilterOptions, "min_month" | "max_month">>(`/lockup/filter-options${buildQs(filters)}`, signal);

export const fetchLockupDateRange = () =>
  getJson<{ min_month: string | null; max_month: string | null }>("/lockup/date-range");

export const fetchLockupMonthly = (f: LockupFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; total_lockup_gbp: number }[] }>(`/lockup/monthly${buildQs(f)}`, signal);

export const fetchLockupMonthlyByAgeBand = (f: LockupFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[] }>(`/lockup/monthly-by-age-band${buildQs(f)}`, signal);

export const fetchLockupRiskConcentration = (f: LockupFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; risk_pct: number }[] }>(`/lockup/risk-concentration${buildQs(f)}`, signal);

export const fetchLockupByOfficeAgeBand = (f: LockupFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[] }>(`/lockup/by-office-age-band${buildQs(f)}`, signal);

export const fetchLockupByServiceLine = (f: LockupFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[]; series: string[] }>(`/lockup/by-service-line${buildQs(f)}`, signal);

export const fetchLockupByLocation = (f: LockupFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[]; series: string[] }>(`/lockup/by-location${buildQs(f)}`, signal);

export const fetchLockupAvgAge = (f: LockupFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; avg_age_days: number }[] }>(`/lockup/avg-age${buildQs(f)}`, signal);

export const LOCKUP_DRILL_FILTER_KEYS = [
  "service_lines",
  "legacy_locations",
  "managers",
  "assignment_categories",
  "age_band_names",
  "date_from",
  "date_to",
] as const;

export const fetchLockupDrillThrough = (
  month: string,
  filters?: Partial<LockupFilters> & { managerSort?: SortState; clientSort?: SortState },
  signal?: AbortSignal,
  page?: { limit: number; skip: number },
) => {
  const { managerSort, clientSort, ...baseFilters } = filters ?? {};
  const params = buildDrillThroughParams({
    month,
    filters: baseFilters as Record<string, string[] | string | null | undefined>,
    filterKeys: [...LOCKUP_DRILL_FILTER_KEYS],
  });
  if (page) {
    params.set("limit", String(page.limit));
    params.set("skip", String(page.skip));
  }
  if (managerSort) {
    appendSortParams(params, managerSort, { sortByParam: "manager_sort_by", sortDirParam: "manager_sort_dir" });
  }
  if (clientSort) {
    appendSortParams(params, clientSort, { sortByParam: "client_sort_by", sortDirParam: "client_sort_dir" });
  }
  return getJson<LockupDrillThroughResponse>(`/lockup/drill-through?${params.toString()}`, signal);
};

export const downloadLockupDrillThroughExcel = (
  month: string,
  filters?: Partial<LockupFilters>,
  exportOptions: Array<"assignment_manager" | "client_assignment"> = [],
) => {
  const params = buildDrillThroughParams({
    month,
    filters: filters as Record<string, string[] | string | null | undefined>,
    filterKeys: [...LOCKUP_DRILL_FILTER_KEYS],
  });
  exportOptions.forEach((option) => params.append("export_options", option));
  return apiDownloadFile(
    `${API_URL}/lockup/drill-through/export?${params.toString()}`,
    exportOptions.length > 1 ? "Lockup Drill Through Exports.zip" : "lockup-drill-through.xlsx",
  );
};
